import React from 'react';

import { ProductivitySupportPreviousWeekFAQSet } from '../faqs/ProductivitySupportPreviousWeekFAQSet';
import { ProductivitySupportCurrentWeekFAQSet } from '../faqs/ProductivitySupportCurrentWeekFAQSet';
import { QualitySupportPreviousWeekFAQSet } from '../faqs/QualitySupportPreviousWeekFAQSet';
import { QualitySupportCurrentWeekFAQSet } from '../faqs/QualitySupportCurrentWeekFAQSet';
import { AdditionalSupportSafetyBehavioralTimeFAQSet } from '../faqs/AdditionalSupportSafetyBehavioralTimeFAQSet';
import { AdditionalSupportProductivityQualityFAQSet } from '../faqs/AdditionalSupportProductivityQualityFAQSet';
import { HelpType } from '../types/backendService';
import { UserDetails } from '../context/GlobalContext';

/**
 * A RequestHelpWorkflowInput object describes an input that is gathered from a user within a workflow.
 */
export interface RequestHelpWorkflowInput {
  id: string; // identifier of the input - should be unique in the scope of a RequestHelpWorkflow
  text: string; // text to display to the user
}

/**
 * A RequestHelpWorkflow object describes a workflow that a user can step through to get help for
 * a particular aspect of their job.
 */
export interface RequestHelpWorkflow {
  text: string;
  faqSet?: React.FunctionComponent;
  helpType: HelpType;
  title: string;
  inputs?: RequestHelpWorkflowInput[];
  actionDetailsGenerator?: (inputs: Map<string, string>) => Record<string, string>;
  supplementalUrlsGenerator?: (userDetails: UserDetails) => Record<string, string>;
  completionMessage: string;
}

/**
 * A RequestHelpSection object describes a top-level grouping of a series
 * of related workflows. For example: "Productivity Support" is a grouping of
 * workflows for getting help related to productivity.
 */
export interface RequestHelpSection {
  name: string;
  workflows: RequestHelpWorkflow[];
}

/**
 * Configuration that defines how to render the Request Help UI.
 *
 * The Request Help UI is divided into multiple "sections" of related "workflows".
 *
 * The user first selects a "section" before being able to choose a particular "item".
 * If the item has a "FAQ set" then these will be displayed to the user.
 * Next, if an item has "inputs" associated with it then the UI will ask the user to fill in these inputs.
 * Then the UI will submit a request to the backend to create an Action. The ActionDetails passed to the API will be generated
 * based on the values that the user entered as inputs - the HelpType and ActionDetails to pass to the backend for
 * each workflow can be defined below.
 */
export const REQUEST_HELP_CONFIG: RequestHelpSection[] = [
  {
    name: "productivitySupport",
    workflows: [
      {
        text: "previousWeekDataQuestion",
        faqSet: ProductivitySupportPreviousWeekFAQSet,
        helpType: HelpType.PREVIOUS_WEEK_PRODUCTIVITY_DATA_QUESTION,
        title: "PAK Associate Question - Productivity, Previous Week",
        supplementalUrlsGenerator: (userDetails: UserDetails) => {
          return {
            "ADAPT": `https://adapt-iad.amazon.com/#/employee-dashboard/${userDetails.login}`,
            "FCLM": `https://fclm-portal.amazon.com/employee/timeDetails?employeeId=${userDetails.employeeId}`,
            "Roboscout 2.0 - Station Work": `https://roboscout.amazon.com/ui/station-work?sites=(${userDetails.warehouseId})`,
            "Vantage": "https://vantage.amazon.com/app/fulfillment-dashboards/station-map",
            "Apollo Audit": "https://apollo-audit.corp.amazon.com",
          };
        },
        completionMessage: "generalCompletionMessage",
      },
      {
        text: "currentWeekDataQuestion",
        faqSet: ProductivitySupportCurrentWeekFAQSet,
        helpType: HelpType.CURRENT_WEEK_PRODUCTIVITY_DATA_QUESTION,
        title: "PAK Associate Question - Productivity, Current Week",
        supplementalUrlsGenerator: (userDetails: UserDetails) => {
          return {
            "ADAPT": `https://adapt-iad.amazon.com/#/employee-dashboard/${userDetails.login}`,
            "FCLM": `https://fclm-portal.amazon.com/employee/timeDetails?employeeId=${userDetails.employeeId}`,
            "Roboscout 2.0 - Station Work": `https://roboscout.amazon.com/ui/station-work?sites=(${userDetails.warehouseId})`,
            "Vantage": "https://vantage.amazon.com/app/fulfillment-dashboards/station-map",
            "Apollo Audit": "https://apollo-audit.corp.amazon.com",
          };
        },
        completionMessage: "generalCompletionMessage",
      },
    ]
  },
  {
    name: "qualitySupport",
    workflows: [
      {
        text: "previousWeekDataQuestion",
        faqSet: QualitySupportPreviousWeekFAQSet,
        helpType: HelpType.PREVIOUS_WEEK_QUALITY_DATA_QUESTION,
        title: "PAK Associate Question - Quality, Previous Week",
        supplementalUrlsGenerator: (userDetails: UserDetails) => {
          return {
            "ADAPT": `https://adapt-iad.amazon.com/#/employee-dashboard/${userDetails.login}`,
            "FCLM": `https://fclm-portal.amazon.com/employee/timeDetails?employeeId=${userDetails.employeeId}`,
            "Roboscout 2.0 - Station Work": `https://roboscout.amazon.com/ui/station-work?sites=(${userDetails.warehouseId})`,
            "Vantage": "https://vantage.amazon.com/app/fulfillment-dashboards/station-map",
            "Apollo Audit": "https://apollo-audit.corp.amazon.com",
          };
        },
        completionMessage: "generalCompletionMessage",
      },
      {
        text: "currentWeekDataQuestion",
        faqSet: QualitySupportCurrentWeekFAQSet,
        helpType: HelpType.CURRENT_WEEK_QUALITY_DATA_QUESTION,
        title: "PAK Associate Question - Quality, Current Week",
        supplementalUrlsGenerator: (userDetails: UserDetails) => {
          return {
            "ADAPT": `https://adapt-iad.amazon.com/#/employee-dashboard/${userDetails.login}`,
            "FCLM": `https://fclm-portal.amazon.com/employee/timeDetails?employeeId=${userDetails.employeeId}`,
            "Roboscout 2.0 - Station Work": `https://roboscout.amazon.com/ui/station-work?sites=(${userDetails.warehouseId})`,
            "Vantage": "https://vantage.amazon.com/app/fulfillment-dashboards/station-map",
            "Apollo Audit": "https://apollo-audit.corp.amazon.com",
          };
        },
        completionMessage: "generalCompletionMessage",
      },
    ]
  },
  {
    name: "additionalSupport",
    workflows: [
      {
        text: "sbtFeedbackQuestion",
        faqSet: AdditionalSupportSafetyBehavioralTimeFAQSet,
        helpType: HelpType.SBT_FEEDBACK_QUESTION,
        title: "PAK Associate Question - Safety, Policy, or Time and Attendance",
        supplementalUrlsGenerator: (userDetails: UserDetails) => {
          return {
            "MyTime": "https://w.amazon.com/index.php/MyTime%20App",
            "AUSTIN Wiki": "https://w.amazon.com/bin/view/AWSHealthandSafety/TechandTools/AUSTIN",
            "ADAPT": `https://adapt-iad.amazon.com/#/employee-dashboard/${userDetails.login}`,
            "FCLM": `https://fclm-portal.amazon.com/employee/timeDetails?employeeId=${userDetails.employeeId}`,
            "Roboscout 2.0 - Station Work": `https://roboscout.amazon.com/ui/station-work?sites=(${userDetails.warehouseId})`,
            "Vantage": "https://vantage.amazon.com/app/fulfillment-dashboards/station-map",
            "Apollo Audit": "https://apollo-audit.corp.amazon.com",
          };
        },
        completionMessage: "generalCompletionMessage",
      },
      {
        text: "pqFeedbackQuestion",
        faqSet: AdditionalSupportProductivityQualityFAQSet,
        helpType: HelpType.PQ_FEEDBACK_QUESTION,
        title: "PAK Associate Question - Productivity or Quality Feedback",
        supplementalUrlsGenerator: (userDetails: UserDetails) => {
          return {
            "ADAPT": `https://adapt-iad.amazon.com/#/employee-dashboard/${userDetails.login}`,
            "FCLM": `https://fclm-portal.amazon.com/employee/timeDetails?employeeId=${userDetails.employeeId}`,
            "Roboscout 2.0 - Station Work": `https://roboscout.amazon.com/ui/station-work?sites=(${userDetails.warehouseId})`,
            "Vantage": "https://vantage.amazon.com/app/fulfillment-dashboards/station-map",
            "Apollo Audit": "https://apollo-audit.corp.amazon.com",
          };
        },
        completionMessage: "generalCompletionMessage",
      },
      {
        text: "productivityRetrain",
        helpType: HelpType.PRODUCTIVITY_RETRAIN,
        title: "PAK Associate Question - Productivity Retrain",
        inputs: [
          {
            text: "processPathInputText",
            id: "process-path",
          }
        ],
        actionDetailsGenerator: (inputs: Map<string, string>) => {
          return {
            "Process Path": inputs.get("process-path")!
          };
        },
        supplementalUrlsGenerator: (userDetails: UserDetails) => {
          return {
            "ADAPT": `https://adapt-iad.amazon.com/#/employee-dashboard/${userDetails.login}`,
            "FCLM": `https://fclm-portal.amazon.com/employee/timeDetails?employeeId=${userDetails.employeeId}`,
            "Roboscout 2.0 - Station Work": `https://roboscout.amazon.com/ui/station-work?sites=(${userDetails.warehouseId})`,
            "Vantage": "https://vantage.amazon.com/app/fulfillment-dashboards/station-map",
            "Apollo Audit": "https://apollo-audit.corp.amazon.com",
          };
        },
        completionMessage: "retrainCompletionMessage",
      },
      {
        text: "qualityRetrain",
        helpType: HelpType.QUALITY_RETRAIN,
        title: "PAK Associate Question - Quality Retrain",
        inputs: [
          {
            text: "processPathInputText",
            id: "process-path",
          }
        ],
        actionDetailsGenerator: (inputs: Map<string, string>) => {
          return {
            "Process Path": inputs.get("process-path")!
          };
        },
        supplementalUrlsGenerator: (userDetails: UserDetails) => {
          return {
            "ADAPT": `https://adapt-iad.amazon.com/#/employee-dashboard/${userDetails.login}`,
            "FCLM": `https://fclm-portal.amazon.com/employee/timeDetails?employeeId=${userDetails.employeeId}`,
            "Roboscout 2.0 - Station Work": `https://roboscout.amazon.com/ui/station-work?sites=(${userDetails.warehouseId})`,
            "Vantage": "https://vantage.amazon.com/app/fulfillment-dashboards/station-map",
            "Apollo Audit": "https://apollo-audit.corp.amazon.com",
          };
        },
        completionMessage: "retrainCompletionMessage",
      },
    ]
  }
];
